<template>
  <div class="subscription-view">
    <div v-if="loading" v-loading="loading"
      :element-loading-text="t('subscription.loading')" element-loading-background="rgba(255, 255, 255, 0.9)"
      element-loading-svg-view-box="-10, -10, 50, 50" :element-loading-svg="loadingSvg" class="loading-container">
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    </div>

    <div v-else class="subscription-content">
      <!-- Subscription Summary Header -->
      <div v-if="loading">
        <el-skeleton :rows="2" animated />
      </div>
      <div v-else-if="!currentSubscription" class="no-subscription">
        <el-empty :description="t('subscription.noSubscription')" />
      </div>
      <div v-else class="subscription-summary">
        <!-- Status Header -->
        <div class="status-header" :class="currentSubscription.status">
          <div class="status-left">
            <div class="status-badge" :class="currentSubscription.status">
              <el-icon class="status-icon">
                <component :is="currentSubscription.status === 'active' ? 'Check' : 'Warning'" />
              </el-icon>
              <span class="status-text">
                {{
                  currentSubscription.status === 'active' ? t('subscription.details.active') :
                    currentSubscription.status === 'cancelled' ? t('subscription.details.cancelled') :
                      currentSubscription.status === 'expired' ? t('subscription.details.expired') :
                        t('subscription.details.inactive')
                }}
              </span>
            </div>
            <span class="renewal-info">
              {{ t('subscription.details.renewalDate') }}: <strong>{{ formatDate(currentSubscription.end_date) }}</strong>
            </span>
          </div>
          <div class="status-actions">
            <el-button 
              v-if="currentSubscription?.status !== 'cancelled'" 
              type="danger"
              size="small"
              plain
              @click="handleCancelSubscription"
              :loading="loading" 
              :disabled="loading"
            >
              <el-icon><Close /></el-icon>
              {{ t('subscription.details.cancel') }}
            </el-button>
            <el-button 
              v-if="currentSubscription?.status === 'cancelled'" 
              type="success"
              size="small"
              @click="handleActivateSubscription" 
              :loading="loading" 
              :disabled="loading"
            >
              {{ t('subscription.details.activate') }}
            </el-button>
          </div>
        </div>

        <!-- Info Cards Grid -->
        <div class="info-cards-grid">
          <!-- User Count Card -->
          <div class="info-card">
            <div class="card-icon user-icon">
              <el-icon><User /></el-icon>
            </div>
            <div class="card-content">
              <span class="card-label">{{ t('subscription.details.userCount') }}</span>
              <div class="card-value-row">
                <span class="card-value">{{ currentSubscription.user_count }}</span>
                <el-button 
                  v-if="currentSubscription?.status === 'active'" 
                  size="small" 
                  type="primary" 
                  text 
                  @click="openEditDialog"
                  class="edit-btn"
                >
                  <el-icon><Edit /></el-icon>
                </el-button>
              </div>
            </div>
          </div>

          <!-- Period Card (only for cloud) -->
          <div class="info-card" v-if="showPaymentInfo">
            <div class="card-icon period-icon">
              <el-icon><Calendar /></el-icon>
            </div>
            <div class="card-content">
              <span class="card-label">{{ t('subscription.details.period') }}</span>
              <div class="card-value-row">
                <span class="card-value">{{ currentSubscription.duration_unit === PackageDurationType.MONTH ? t('subscription.details.monthly') : t('subscription.details.yearly') }}</span>
                <el-button 
                  v-if="currentSubscription?.status === 'active'" 
                  size="small" 
                  type="primary" 
                  text 
                  @click="openRenewalDialog"
                  class="edit-btn"
                >
                  <el-icon><Edit /></el-icon>
                </el-button>
              </div>
            </div>
          </div>

          <!-- Fee Card (only for cloud) -->
          <div class="info-card fee-card" v-if="showPaymentInfo">
            <div class="card-icon fee-icon">
              <el-icon><Wallet /></el-icon>
            </div>
            <div class="card-content">
              <span class="card-label">{{ currentSubscription.duration_unit === PackageDurationType.MONTH ? t('subscription.details.monthly') : t('subscription.details.yearly') }} {{ t('subscription.details.fee') }}</span>
              <span class="card-value highlight">{{ 
                currentSubscription.duration_unit === PackageDurationType.MONTH ?
                  formatCurrency(currentSubscription.price.price * currentSubscription.user_count, orderCurrencyCode) :
                  formatCurrency(currentSubscription.price.price * currentSubscription.user_count * 12, orderCurrencyCode)
              }}</span>
              <span class="card-sub">{{ formatCurrency(currentSubscription.price.price, orderCurrencyCode) }}{{ t('subscription.details.perUserPerMonth') }}</span>
            </div>
          </div>

          <!-- Dealer Card -->
          <div class="info-card dealer-card" v-if="currentSubscription.company?.dealer">
            <div class="card-icon dealer-icon">
              <el-icon><Shop /></el-icon>
            </div>
            <div class="card-content">
              <span class="card-label">{{ t('subscription.dealer.title') }}</span>
              <span class="card-value">{{ currentSubscription.company.dealer.name }}</span>
              <div class="dealer-contacts">
                <a v-if="currentSubscription.company.dealer.phone || currentSubscription.company.dealer.authorized_person_phone" 
                   class="contact-link" 
                   :href="'tel:' + (currentSubscription.company.dealer.authorized_person_phone || currentSubscription.company.dealer.phone)">
                  <el-icon><Phone /></el-icon>
                </a>
                <a v-if="currentSubscription.company.dealer.email" 
                   class="contact-link" 
                   :href="'mailto:' + currentSubscription.company.dealer.email">
                  <el-icon><Message /></el-icon>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Changes Alert -->
        <div v-if="pendingUpgrades.length > 0" class="pending-changes-section">
          <el-alert type="warning" :closable="false" show-icon class="pending-alert">
            <template #title>
              <div class="pending-header">
                <span class="pending-title">{{ t('subscription.pendingChanges.title') }}</span>
              </div>
            </template>
            <div class="pending-list">
              <div class="pending-item" v-for="upgrade in pendingUpgrades" :key="upgrade.id">
                <div class="pending-info">
                  <span class="pending-change">
                    <strong>{{ upgrade.old_user_count }}</strong> → <strong>{{ upgrade.new_user_count }}</strong> {{ t('subscription.pendingChanges.user') }}
                  </span>
                  <span class="pending-amount" v-if="showPaymentInfo && upgrade.order_amount">
                    {{ t('subscription.pendingChanges.difference') }}: {{ formatCurrency(upgrade.order_amount, orderCurrencyCode) }}
                  </span>
                </div>
                <el-button 
                  type="danger" 
                  size="small" 
                  plain
                  @click="handleCancelUpgrade(upgrade)"
                  :loading="cancellingUpgradeId === upgrade.id"
                >
                  {{ t('subscription.pendingChanges.revert') }}
                </el-button>
              </div>
            </div>
          </el-alert>
        </div>
      </div>

      <!-- Orders Section - Cloud paketlerde veya bayi yoksa göster -->
      <div class="orders-section" v-if="showPaymentInfo">
        <div class="orders-header">
          <h3>{{ t('subscription.orders.title') }}</h3>
          <el-switch
            v-model="compactView"
            :active-text="t('subscription.orders.compact')"
            :inactive-text="t('subscription.orders.normal')"
            size="small"
          />
        </div>
        <div class="orders-content">
          <div class="orders-table-wrapper">
            <el-table :data="orders" style="width: 100%" @selection-change="handleSelectionChange"
              :row-class-name="getRowClass" size="small" :class="{ 'compact-view': compactView }">
              <el-table-column type="selection" width="40" :selectable="(row: Order) => !row.paided"
                :show-overflow-tooltip="false">
                <template #default="{ row }">
                  <el-checkbox :style="{ display: row.paided ? 'none' : 'block' }"
                    :model-value="selectedOrders.some(order => order.id === row.id)"
                    @change="(val: boolean) => handleSelectionChange(val ? [...selectedOrders, row] : selectedOrders.filter(order => order.id !== row.id))" />
                </template>
              </el-table-column>
              <el-table-column prop="order_no" :label="t('subscription.orders.orderNo')" width="130" class-name="order-column" />
              <el-table-column prop="order_date" :label="t('subscription.orders.date')" width="90" class-name="order-column">
                <template #default="{ row }">
                  {{ formatDate(row.order_date) }}
                </template>
              </el-table-column>
              <el-table-column prop="order_amount" :label="t('subscription.orders.amount')" width="100" class-name="order-column">
                <template #default="{ row }">
                  {{ formatCurrency(row.order_amount, orderCurrencyCode) }}
                </template>
              </el-table-column>
              <el-table-column prop="order_details" :label="t('subscription.orders.description')" class-name="order-column" :show-overflow-tooltip="true">
                <template #default="{ row }">
                  <div class="order-description">
                    <div class="description-main">{{ row.order_details.description }}</div>
                    <div class="order-details-info" v-if="!compactView">
                      <small class="text-muted">
                        {{ row.order_details.amount }} {{ t('subscription.editDialog.userRange') }} × {{ formatCurrency(row.order_details.price, orderCurrencyCode)
                        }}{{ t('subscription.details.perUserPerMonth') }}
                      </small>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="payments" :label="t('subscription.orders.paymentDate')" width="90" class-name="order-column">
                <template #default="{ row }">
                  {{ row.payments[0]?.payment_date ? formatDate(row.payments[0]?.payment_date) : '-' }}
                </template>
              </el-table-column>
              <el-table-column :label="t('subscription.orders.status')" width="90" class-name="order-column">
                <template #default="{ row }">
                    <el-tag :type="row.paided ? 'success' : 'danger'" size="small" effect="plain">
                      {{ row.paided ? t('subscription.orders.paid') : t('subscription.orders.unpaid') }}
                    </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="t('subscription.orders.actions')" width="130" class-name="order-column" align="center">
                <template #default="{ row }">
                  <el-button 
                    v-if="!row.paided"
                    size="small" 
                    type="success" 
                    @click="paySingleOrder(row)"
                    style="width: 100%;"
                  >
                    <el-icon><CreditCard /></el-icon>
                    {{ t('subscription.orders.pay') }}
                  </el-button>
                  <el-button 
                    v-if="row.paided"
                    size="small" 
                    type="primary" 
                    @click="showPrintDialog(row)"
                    link
                  >
                    <el-icon><Printer /></el-icon>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="pagination-container">
              <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :page-sizes="pageSizes"
                layout="total, sizes, prev, pager, next" :total="orders.length"
                @size-change="handleSizeChange" @current-change="handleCurrentChange" background small
                hide-on-single-page />
            </div>
          </div>
          <!-- Inline Payment Summary -->
          <div class="payment-summary-inline" v-if="selectedOrders.length > 0">
            <div class="summary-info">
              <span class="summary-count">{{ selectedOrders.length }} {{ t('subscription.selectedOrders.count') }}</span>
              <span class="summary-total">{{ formatCurrency(selectedOrdersTotal, orderCurrencyCode) }}</span>
            </div>
            <el-button 
              type="success" 
              @click="handlePaySelectedOrders"
              size="small"
            >
              <el-icon><CreditCard /></el-icon>
              {{ t('subscription.selectedOrders.paySelected') }}
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Subscription Dialog -->
  <el-dialog v-model="editDialogVisible" :title="showPaymentInfo ? t('subscription.editDialog.title') : t('subscription.editDialog.titleNoPayment')" width="500px" :close-on-click-modal="false" :close-on-press-escape="false">
    <el-form :model="editForm" label-position="top">
      <!-- Fiyat seçimi göster (Cloud paket veya bayi yoksa) -->
      <el-form-item :label="t('subscription.editDialog.priceSelection')" v-if="showPaymentInfo">
        <div class="price-options">
          <el-radio-group v-model="editForm.selectedPriceId" @change="handlePriceChange">
            <el-radio 
              v-for="price in filteredPriceList" 
              :key="price.id" 
              :value="price.id"
              class="price-radio"
            >
              {{ price.min_user_count }}-{{ price.max_user_count }} {{ t('subscription.editDialog.userRange') }} ({{ formatCurrency(price.price, orderCurrencyCode) }}{{ t('subscription.details.perUserPerMonth') }})
            </el-radio>
          </el-radio-group>
        </div>
      </el-form-item>

      <el-form-item :label="t('subscription.editDialog.userCount')">
        <div class="user-count-options">
          <el-radio-group v-model="editForm.newUserCount" @change="calculatePriceDifference">
            <el-radio 
              v-for="userCount in getUserCountRange()" 
              :key="userCount" 
              :value="userCount"
              class="user-count-radio"
            >
              <!-- Fiyat göster (Cloud paket veya bayi yoksa) -->
              <template v-if="showPaymentInfo">
                {{ userCount }} {{ t('subscription.editDialog.userRange') }} - {{ formatCurrency(userCount * (selectedPrice?.price || 0), orderCurrencyCode) }}/{{ t('subscription.details.monthly').toLowerCase() }}
              </template>
              <template v-else>
                {{ userCount }} {{ t('subscription.editDialog.userRange') }}
              </template>
            </el-radio>
          </el-radio-group>
        </div>
        <el-tag type="info" size="small" style="margin-top: 8px; white-space: normal; height: auto; line-height: 1.4;">
          {{ t('subscription.editDialog.minMax').replace('{min}', Math.max(1, employeeCount).toString()).replace('{max}', (selectedPrice?.max_user_count || 50).toString()) }}
        </el-tag>
      </el-form-item>
    </el-form>

    <!-- Fiyat farkı uyarısı sadece Cloud paketlerde göster -->
    <el-alert type="warning" :closable="false" show-icon class="mb-4" v-if="showPaymentInfo && priceDifference > 0">
      <template #title>
        <div class="price-difference-info">
          <p v-html="t('subscription.editDialog.priceDifference').replace('{userDiff}', userDiff.toString()).replace('{daysDiff}', daysDiff.toString()).replace('{amount}', formatCurrency(priceDifference, orderCurrencyCode))"></p>
        </div>
      </template>
    </el-alert>

    <!-- Bayili paketler için bilgi mesajı (cloud değil ve bayi var) -->
    <el-alert type="info" :closable="false" show-icon class="mb-4" v-if="!showPaymentInfo">
      <template #title>
        <div>
          <p>{{ t('subscription.editDialog.dealerInfo') }}</p>
        </div>
      </template>
    </el-alert>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="editDialogVisible = false">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleConfirmEdit" :loading="loading"
          :disabled="loading || editForm.newUserCount == currentSubscription?.user_count">
          {{ showPaymentInfo ? t('subscription.editDialog.saveChanges') : t('subscription.editDialog.sendRequest') }}
        </el-button>
      </span>
    </template>
  </el-dialog>

  <!-- reneval duration dialog - Cloud paketlerde veya bayi yoksa göster -->
  <el-dialog v-if="showPaymentInfo" v-model="renewalDurationDialogVisible" :title="t('subscription.renewalDialog.title')" width="500px" :close-on-click-modal="false" :close-on-press-escape="false">
    <el-form :model="renewalDurationForm" label-position="top">
      <el-form-item :label="t('subscription.renewalDialog.renewalPeriod')">
        <div class="duration-options">
          <el-radio-group v-model="renewalDurationForm.newDurationUnit">
            <el-radio 
              v-for="option in durationUnitOptions" 
              :key="option.value" 
              :value="option.value"
              class="duration-radio"
            >
              {{ option.value === PackageDurationType.MONTH ? t('subscription.details.monthly') : t('subscription.details.yearly') }}
            </el-radio>
          </el-radio-group>
        </div>
      </el-form-item>
    </el-form>
    <el-alert type="warning" :closable="false" show-icon class="mb-4">
      <p>{{ t('subscription.renewalDialog.notice') }}</p>
    </el-alert>
    <template #footer>
      <el-button @click="renewalDurationDialogVisible = false">{{ t('common.cancel') }}</el-button>
      <el-button type="primary" @click="handleConfirmRenewalDuration" :loading="loading" :disabled="loading">
        {{ t('subscription.editDialog.saveChanges') }}
      </el-button>
    </template>
  </el-dialog>

  <!-- Print Dialog - Sadece Cloud paketlerde göster -->
  <el-dialog v-if="showPaymentInfo" v-model="printDialogVisible" :title="t('subscription.printDialog.title')" width="600px" :close-on-click-modal="false">
    <div v-if="selectedOrderForPrint" class="print-content">
      <div class="print-header">
        <h3>{{ t('subscription.printDialog.success') }}</h3>
        <p>{{ t('subscription.printDialog.activated') }}</p>
      </div>
      
      <div class="print-details">
        <h4>{{ t('subscription.printDialog.orderDetails') }}</h4>
        <div class="detail-row">
          <span class="label">{{ t('subscription.printDialog.orderNo') }}:</span>
          <span class="value">{{ selectedOrderForPrint.id }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ t('subscription.printDialog.date') }}:</span>
          <span class="value">{{ formatDate(new Date(selectedOrderForPrint.order_date)) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ t('subscription.printDialog.amount') }}:</span>
          <span class="value">{{ formatCurrency(selectedOrderForPrint.order_amount, orderCurrencyCode) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ t('subscription.printDialog.description') }}:</span>
          <span class="value">{{ selectedOrderForPrint.order_details.description }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ t('subscription.printDialog.userCount') }}:</span>
          <span class="value">{{ selectedOrderForPrint.order_details.amount }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ t('subscription.printDialog.unitPrice') }}:</span>
          <span class="value">{{ formatCurrency(selectedOrderForPrint.order_details.price) }}</span>
        </div>
      </div>

      <div v-if="selectedOrderPaymentDetails?.payments?.length > 0" class="print-details">
        <h4>{{ t('subscription.printDialog.paymentDetails') }}</h4>
        <div v-for="payment in selectedOrderPaymentDetails.payments" :key="payment.id" class="payment-detail">
          <div class="detail-row">
            <span class="label">{{ t('subscription.printDialog.transactionId') }}:</span>
            <span class="value">{{ payment.transaction_id || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">{{ t('subscription.printDialog.paymentDate') }}:</span>
            <span class="value">{{ payment.payment_date ? formatDate(new Date(payment.payment_date)) : '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">{{ t('subscription.printDialog.card') }}:</span>
            <span class="value">{{ payment.maskedCreditCard || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">{{ t('subscription.printDialog.status') }}:</span>
            <span class="value">{{ getPaymentStatusText(payment.status) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">{{ t('subscription.printDialog.amount') }}:</span>
            <span class="value">{{ formatCurrency(payment.amount) }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <template #footer>
      <el-button @click="printDialogVisible = false">{{ t('subscription.printDialog.close') }}</el-button>
      <el-button type="primary" @click="printReceipt" :loading="printing">
        <el-icon class="me-2"><Printer /></el-icon>
        {{ t('subscription.printDialog.print') }}
      </el-button>
    </template>
  </el-dialog>

  <!-- Payment Method Selection Dialog -->
  <el-dialog 
    v-if="showPaymentInfo"
    v-model="paymentMethodDialogVisible" 
    :title="t('subscription.paymentMethod.title')" 
    width="500px" 
    :close-on-click-modal="false" 
    :close-on-press-escape="false"
    :before-close="() => { paymentMethodDialogVisible = false; }"
    destroy-on-close
  >
    <div class="payment-method-selection">
      <div class="payment-method-options">
        <div 
          v-if="hasPosSettings"
          class="payment-method-card" 
          :class="{ 'selected': selectedPaymentMethod === 'vpos' }"
          @click="selectedPaymentMethod = 'vpos'"
        >
          <div class="payment-method-icon">
            <el-icon :size="40"><CreditCard /></el-icon>
          </div>
          <div class="payment-method-info">
            <h3>{{ t('subscription.paymentMethod.creditCard') }}</h3>
            <p>{{ t('subscription.paymentMethod.creditCardDesc') }}</p>
          </div>
          <div class="payment-method-radio">
            <el-radio :model-value="selectedPaymentMethod === 'vpos'" @change="selectedPaymentMethod = 'vpos'" />
          </div>
        </div>
        
        <div 
          class="payment-method-card" 
          :class="{ 'selected': selectedPaymentMethod === 'iban' }"
          @click="selectedPaymentMethod = 'iban'"
        >
          <div class="payment-method-icon">
            <el-icon :size="40"><Money /></el-icon>
          </div>
          <div class="payment-method-info">
            <h3>{{ t('subscription.paymentMethod.iban') }}</h3>
            <p>{{ t('subscription.paymentMethod.ibanDesc') }}</p>
          </div>
          <div class="payment-method-radio">
            <el-radio :model-value="selectedPaymentMethod === 'iban'" @change="selectedPaymentMethod = 'iban'" />
          </div>
        </div>
      </div>
      
      <div class="selected-amount-info">
        <div class="amount-row">
          <span class="label">{{ t('subscription.paymentMethod.totalAmount') }}:</span>
          <span class="value">{{ formatCurrency(selectedOrdersTotal, orderCurrencyCode) }}</span>
        </div>
        <!-- <div v-if="selectedOrdersTotalTry" class="amount-row try-row">
          <span class="label"></span>
          <span class="value try-value">≈ {{ formatCurrency(selectedOrdersTotalTry) }}</span>
        </div> -->
      </div>
    </div>
    
    <template #footer>
      <el-button @click="paymentMethodDialogVisible = false">{{ t('common.cancel') }}</el-button>
      <el-button 
        type="primary" 
        @click="proceedWithPayment" 
        :disabled="!selectedPaymentMethod"
        :loading="loading"
      >
        {{ t('subscription.paymentMethod.continue') }}
      </el-button>
    </template>
  </el-dialog>

  <!-- IBAN Payment Dialog - Sadece Cloud paketlerde göster -->
  <el-dialog v-if="showPaymentInfo" v-model="ibanPaymentDialogVisible" :title="t('subscription.ibanDialog.title')" width="500px" :close-on-click-modal="false" :close-on-press-escape="false">
    <div v-if="ibanPaymentData" class="iban-payment-content compact">
      <div class="payment-info">
        
        <!-- Sipariş Özeti -->
        <div class="order-summary-compact">
          <div class="summary-row">
            <span>{{ t('subscription.ibanDialog.order') }}:</span>
            <span>{{ ibanPaymentData.orderNumbers?.join(', ') || ibanPaymentData.orderIds?.join(', ') }}</span>
          </div>
          <div class="summary-row total">
            <span>{{ t('subscription.ibanDialog.amount') }}:</span>
            <span>
              {{ formatCurrency(ibanPaymentData.amount, ibanPaymentData.currency) }}
              <template v-if="ibanPaymentData.currency !== 'TRY'">
                <small class="try-equivalent">(≈ {{ formatCurrency(ibanPaymentData.amountTry) }})</small>
              </template>
            </span>
          </div>
        </div>

        <!-- IBAN Seçimi -->
        <div class="iban-section">
          <!-- TRY Hesabı -->
          <div 
            class="iban-card selectable" 
            :class="{ 'selected': ibanPaymentForm.selectedAccount === 'TRY' }"
            @click="ibanPaymentForm.selectedAccount = 'TRY'"
          >
            <div class="iban-card-header">
              <div class="iban-select-row">
                <el-radio v-model="ibanPaymentForm.selectedAccount" value="TRY" />
                <span class="bank-info">İş Bankası - JBI YAZILIM</span>
              </div>
              <span class="currency-badge try">TRY</span>
            </div>
            <div class="iban-row">
              <span class="iban-text">TR17 0006 4000 0013 2030 4561 79</span>
              <el-button size="small" text @click.stop="copyIban('TR17000640000132030456179')">
                <el-icon><CopyDocument /></el-icon>
              </el-button>
            </div>
            <div class="iban-amount-display">
              <template v-if="ibanPaymentData.currency === 'TRY'">
                {{ formatCurrency(ibanPaymentData.amount) }}
              </template>
              <template v-else>
                {{ formatCurrency(ibanPaymentData.amountTry) }}
              </template>
            </div>
          </div>
          
          <!-- USD Hesabı -->
          <div 
            class="iban-card selectable" 
            :class="{ 'selected': ibanPaymentForm.selectedAccount === 'USD' }"
            @click="ibanPaymentForm.selectedAccount = 'USD'"
          >
            <div class="iban-card-header">
              <div class="iban-select-row">
                <el-radio v-model="ibanPaymentForm.selectedAccount" value="USD" />
                <span class="bank-info">İş Bankası - JBI YAZILIM</span>
              </div>
              <span class="currency-badge usd">USD</span>
            </div>
            <div class="iban-row">
              <span class="iban-text">TR59 0006 4000 0023 2030 1767 37</span>
              <el-button size="small" text @click.stop="copyIban('TR59000640000232030176737')">
                <el-icon><CopyDocument /></el-icon>
              </el-button>
            </div>
            <div class="iban-amount-display">
              {{ formatCurrency(ibanPaymentData.amount, ibanPaymentData.currency) }}
              <template v-if="ibanPaymentData.currency !== 'TRY'">
                <div class="try-info">≈ {{ formatCurrency(ibanPaymentData.amountTry, 'TRY') }} <small>({{ t('subscription.ibanDialog.exchangeRate') }}: {{ ibanPaymentData.exchangeRate?.toFixed(2) }})</small></div>
              </template>
            </div>
          </div>
        </div>

        <!-- Ödeme Bilgileri -->
        <div class="payment-form-compact">
          <el-form label-position="top">
            <el-row :gutter="12">
              <el-col :span="12">
                <el-form-item :label="t('subscription.ibanDialog.paymentDate')" required>
                  <el-date-picker 
                    v-model="ibanPaymentForm.paymentDate" 
                    type="date" 
                    :placeholder="t('subscription.ibanDialog.selectDate')"
                    format="DD.MM.YYYY"
                    value-format="YYYY-MM-DD"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item :label="t('subscription.ibanDialog.receiptNo')">
                  <el-input 
                    v-model="ibanPaymentForm.receiptNumber" 
                    :placeholder="t('subscription.ibanDialog.receiptPlaceholder')"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </div>
      </div>
    </div>
    
    <template #footer>
      <el-button @click="ibanPaymentDialogVisible = false">{{ t('subscription.printDialog.close') }}</el-button>
      <el-button type="primary" @click="sendPaymentNotification" :loading="sendingNotification" :disabled="sendingNotification || !ibanPaymentForm.paymentDate || !ibanPaymentForm.selectedAccount">
        <el-icon class="me-1"><Message /></el-icon>
        {{ t('subscription.ibanDialog.sendNotification') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import api, { adminApi } from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionStore } from '@/stores/subscription';
import { ElNotification, ElMessageBox } from 'element-plus';
import { Close, RefreshRight, Message, CreditCard, Money, Shop, Ticket, Edit, Phone, CopyDocument, Printer, User, Calendar, Wallet } from '@element-plus/icons-vue';
import { formatCurrency } from '@/utils/formatters';
import { Order } from '@/types/order';
import { PackageDurationType } from '@/types/Subscription';
import { useLanguageStore } from '@/stores/language';

const languageStore = useLanguageStore();
const { t } = languageStore;

const router = useRouter();
const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const loading = ref(true);
const error = ref<string | null>(null);
const currentSubscription = ref<any>(null);
const orders = ref<Order[]>([]);
const selectedOrders = ref<Order[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);

// is_cloud kontrolü
const isCloudPackage = computed(() => {
  return currentSubscription.value?.package?.is_cloud ?? true;
});

// Bayi var mı kontrolü
const hasDealer = computed(() => {
  return !!currentSubscription.value?.company?.dealer;
});

// Fatura ve ödeme bilgilerini göster:
// - Cloud paketse VEYA
// - Cloud değilse ama bayi yoksa (direkt PassQR ile muhatap)
const showPaymentInfo = computed(() => {
  return isCloudPackage.value || !hasDealer.value;
});

// Bekleyen (geri alınabilir) yükseltmeler
const pendingUpgrades = ref<any[]>([]);
const cancellingUpgradeId = ref<number | null>(null);
const pageSizes = [5, 10, 20, 50];
const editDialogVisible = ref(false);
const employeeCount = ref<number>(0);
const editForm = ref({
  newUserCount: 0,
  selectedPriceId: null as number | null,
});

const durationUnitOptions = computed(() => [
  { value: PackageDurationType.MONTH, label: t('subscription.details.monthly') },
  { value: PackageDurationType.YEAR, label: t('subscription.details.yearly') }
]);

interface Price {
  id: number;
  price: number;
  min_user_count: number;
  max_user_count: number;
  step: number;
  created_at: string;
}

const priceList = ref<Price[]>([]);

// Dil koduna göre locale mapping
const getLocale = (): string => {
  const langMap: Record<string, string> = {
    'tr': 'tr-TR',
    'en': 'en-US',
    'de': 'de-DE',
    'fr': 'fr-FR',
    'es': 'es-ES',
    'it': 'it-IT',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
    'ru': 'ru-RU',
    'zh': 'zh-CN'
  };
  return langMap[languageStore.currentLanguage] || 'en-US';
};

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString(getLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
};

// Döviz kodu - fiyattan al
const orderCurrencyCode = computed(() => {
  // Fiyatın döviz kodunu kullan
  if (currentSubscription.value?.price?.currency_code) {
    return currentSubscription.value.price.currency_code;
  }
  return 'TRY';
});

// Seçili siparişlerin toplam tutarı (dönüşüm yok - direkt toplam)
const selectedOrdersTotal = computed(() => {
  return selectedOrders.value.reduce((sum, order) => {
    return sum + Number(order.order_amount);
  }, 0);
});

const fetchSubscription = async () => {
  try {
    // Admin-API'den abonelik bilgisi (güvenli)
    const response = await adminApi.get(`/subscriptions/company/${authStore.user.id}`);
    if (response.data) {
      // Reactive güncelleme için yeni obje oluştur
      currentSubscription.value = { ...response.data };
      renewalDurationForm.value.newDurationUnit = currentSubscription.value?.duration_unit;
      editForm.value.selectedPriceId = currentSubscription.value?.price.id;
      editForm.value.newUserCount = currentSubscription.value?.user_count;
      
      // Store'a kaydet ve expiration modal'ı kontrol et
      subscriptionStore.setSubscriptionData(response.data);
      
      // Eğer abonelik aktifse modal'ı kapat
      if (response.data.status === 'active') {
        subscriptionStore.hideExpirationModal();
      } else {
        subscriptionStore.checkAndSetExpirationModal();
      }
    }

    // Admin-API'den fiyatlandırma
    const pricingResponse = await adminApi.get('/pricing/cloud');
    if (pricingResponse.data) {
      priceList.value = pricingResponse.data;
    }
  } catch (err) {
    error.value = t('subscription.messages.loadError');
    console.error('Error fetching subscription:', err);
  }
};

const fetchPendingUpgrades = async () => {
  try {
    // Admin-API'den bekleyen yükseltmeler
    const response = await adminApi.get(`/subscriptions/pending-upgrades/${authStore.user.id}`);
    if (response.data && Array.isArray(response.data)) {
      pendingUpgrades.value = response.data;
    } else {
      pendingUpgrades.value = [];
    }
  } catch (err) {
    console.error('Error fetching pending upgrades:', err);
    pendingUpgrades.value = [];
  }
};

const handleCancelUpgrade = async (upgrade: any) => {
  console.log('handleCancelUpgrade called with:', upgrade);
  try {
    // Aktif çalışan sayısı uyarısı (sadece old_user_count varsa kontrol et)
    console.log('employeeCount:', employeeCount.value, 'old_user_count:', upgrade.old_user_count);
    if (upgrade.old_user_count && employeeCount.value > upgrade.old_user_count) {
      await ElMessageBox.alert(
        t('subscription.messages.cannotRevert').replace('{employeeCount}', employeeCount.value.toString()).replace('{oldCount}', upgrade.old_user_count.toString()),
        t('subscription.messages.cannotRevertTitle'),
        { type: 'warning', confirmButtonText: t('common.ok'), customClass: 'high-z-index-messagebox' }
      );
      return;
    }

    await ElMessageBox.confirm(
      t('subscription.messages.revertConfirm').replace('{newCount}', upgrade.new_user_count.toString()).replace('{oldCount}', upgrade.old_user_count.toString()),
      t('subscription.messages.revertTitle'),
      {
        confirmButtonText: t('subscription.messages.revertButton'),
        cancelButtonText: t('common.cancel'),
        type: 'warning',
        customClass: 'high-z-index-messagebox'
      }
    );

    cancellingUpgradeId.value = upgrade.id;
    
    console.log('Calling API to cancel upgrade:', upgrade.id, 'company_id:', authStore.user.id);
    // Admin-API'den yükseltme iptal
    const response = await adminApi.post(`/subscriptions/upgrade/${upgrade.id}/cancel`, {
      company_id: authStore.user.id
    });
    console.log('Cancel upgrade response:', response.data);

    if (response.data.success) {
      ElNotification.success({
        title: t('common.success'),
        message: t('subscription.messages.revertSuccess')
      });
      // Verileri yenile
      await fetchSubscription();
      await fetchPendingUpgrades();
      await fetchOrders();
    } else {
      ElNotification.error({
        title: t('common.error'),
        message: response.data.message || t('subscription.messages.revertError')
      });
    }
  } catch (err: any) {
    if (err !== 'cancel') {
      console.error('Error cancelling upgrade:', err);
      ElNotification.error({
        title: t('common.error'),
        message: err.response?.data?.message || t('subscription.messages.revertError')
      });
    }
  } finally {
    cancellingUpgradeId.value = null;
  }
};

const fetchEmployeeCount = async () => {
  try {
    const response = await api.get(`/employees/count/${authStore.user.id}`);
    if (response.data) {
      employeeCount.value = response.data.count || 0;
    }
  } catch (err) {
    console.error('Error fetching employee count:', err);
    employeeCount.value = 0;
  }
};

const fetchOrders = async () => {
  try {
    // Admin-API'den siparişler
    const response = await adminApi.get<Order[]>(`/orders/company/${authStore.user.id}`);
    
    if (response.data) {
      orders.value = response.data;
    }
  } catch (err) {
    console.error('Error fetching orders:', err);
  }
};

const fetchPosList = async () => {
  try {
    // Admin-API'den POS ayarları listesi
    const response = await adminApi.get<any[]>('/pos-ayarlari');
    if (response.data) {
      posList.value = response.data || [];
    } else {
      posList.value = [];
    }
  } catch (err) {
    console.error('Error fetching POS list:', err);
    posList.value = [];
  }
};

const handleSelectionChange = (selection: Order[]) => {
  selectedOrders.value = selection.filter(order => !order.paided);
};

const getRowClass = (row: any) => {
  return row.row.paided ? 'paid-row' : 'unpaid-row';
};

const handlePaySelectedOrders = async () => {
  if (selectedOrders.value.length === 0) {
    ElNotification.warning(t('subscription.messages.selectOrder'));
    return;
  }

  // Eğer sipariş dövizi TRY değilse, kur bilgisini al
  currentExchangeRate.value = null;
  if (orderCurrencyCode.value !== 'TRY') {
    try {
      const response = await adminApi.get(`/currencies/${orderCurrencyCode.value}`);
      if (response.data && response.data.rate_to_try) {
        currentExchangeRate.value = Number(response.data.rate_to_try);
      }
    } catch (e) {
      console.error('Kur bilgisi alınamadı:', e);
    }
  }

  // Ödeme yöntemi seçim dialog'unu göster
  selectedPaymentMethod.value = null;
  paymentMethodDialogVisible.value = true;
};

// Tek sipariş için ödeme
const paySingleOrder = async (order: Order) => {
  // Siparişi seçili listeye ekle
  selectedOrders.value = [order];
  
  // Eğer sipariş dövizi TRY değilse, kur bilgisini al
  currentExchangeRate.value = null;
  if (orderCurrencyCode.value !== 'TRY') {
    try {
      const response = await adminApi.get(`/currencies/${orderCurrencyCode.value}`);
      if (response.data && response.data.rate_to_try) {
        currentExchangeRate.value = Number(response.data.rate_to_try);
      }
    } catch (e) {
      console.error('Kur bilgisi alınamadı:', e);
    }
  }

  // Ödeme yöntemi seçim dialog'unu göster
  selectedPaymentMethod.value = null;
  paymentMethodDialogVisible.value = true;
};

const proceedWithPayment = async () => {
  if (!selectedPaymentMethod.value) {
    ElNotification.warning(t('subscription.messages.selectPaymentMethod'));
    return;
  }

  // Dialog'u hemen kapat
  paymentMethodDialogVisible.value = false;
  
  // Dialog'un kapanması için kısa bir bekleme
  await new Promise(resolve => setTimeout(resolve, 100));

  try {
    loading.value = true;

    // Toplam tutarı hesapla (dönüşüm yok - direkt sipariş tutarları)
    const totalAmount = Number(selectedOrdersTotal.value.toFixed(2));
    const currency = orderCurrencyCode.value;
    
    // Kredi kartı ile ödeme seçildiyse, payment session oluştur ve harici sayfaya yönlendir
    if (selectedPaymentMethod.value === 'vpos') {
      try {
        // Payment session oluştur (tutarı ve döviz kodunu direkt gönder)
        const sessionResponse = await adminApi.post('/payment-sessions', {
          companyId: authStore.user.id,
          orderIds: selectedOrders.value.map(order => order.id.toString()),
          orderNumbers: selectedOrders.value.map(order => order.order_no),
          amount: totalAmount,
          currency: currency
        });
        
        const { sessionId } = sessionResponse.data;
        
        if (!sessionId) {
          throw new Error(t('subscription.messages.sessionError'));
        }
        
        loading.value = false;
        
        // Harici ödeme sayfası URL'i (sadece session ID ile)
        const PAYMENT_PAGE_URL = import.meta.env.VITE_PAYMENT_PAGE_URL || 'https://passqr-payment.jbi.com.tr';
        const paymentUrl = `${PAYMENT_PAGE_URL}?sessionId=${sessionId}`;
        
        // Harici sayfaya yönlendir
        window.location.href = paymentUrl;
        return;
      } catch (sessionError: any) {
        console.error('Payment session oluşturma hatası:', sessionError);
        loading.value = false;
        ElNotification({
          title: t('common.error'),
          message: sessionError.response?.data?.message || t('subscription.messages.sessionError'),
          type: 'error',
          duration: 5000,
          customClass: 'high-z-notification'
        });
        return;
      }
    }
    
    // IBAN ödeme yöntemi için API çağrısı yap
    const requestData = {
      companyId: authStore.user.id,
      orderIds: selectedOrders.value.map(order => order.id).join(','),
      amountDue: totalAmount,
      currency: currency,
      paymentMethod: selectedPaymentMethod.value
    };
    
    // Admin-API'den ödeme hazırlama (sadece IBAN için)
    const response = await adminApi.post('/subscriptions/orders-payment', requestData);

    // Ödeme yöntemini kontrol et
    const paymentData = response.data;
    
    if (paymentData.paymentMethod === 'iban') {
      // IBAN ödeme yöntemi
      loading.value = false;
      showIbanPaymentDialog(paymentData.ibanData);
      return;
    }

  } catch (err: any) {
    console.error('proceedWithPayment hatası:', err);
    
    // Daha detaylı hata mesajı
    let errorMessage = t('subscription.messages.paymentError');
    
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.response?.data?.error) {
      errorMessage = err.response.data.error;
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Notification'ı daha yüksek z-index ile göster
    ElNotification({
      title: t('common.error'),
      message: errorMessage,
      type: 'error',
      duration: 5000,
      customClass: 'high-z-notification'
    });
    
    loading.value = false;
  }
};

const handleCancelSubscription = async () => {
  if (!currentSubscription.value) return;

  try {
    const result = await ElMessageBox.prompt(
      t('subscription.messages.cancelConfirm'),
      t('subscription.messages.cancelTitle'),
      {
        confirmButtonText: t('subscription.messages.cancelButton'),
        cancelButtonText: t('common.cancel'),
        type: 'warning',
        inputType: 'textarea',
        inputPlaceholder: t('subscription.messages.cancelReasonPlaceholder'),
        inputValidator: (value) => {
          if (!value || value.trim().length < 1) {
            return t('subscription.messages.cancelReasonRequired');
          }
          return true;
        },
        customClass: 'high-z-dialog',
        appendTo: 'body'
      }
    );

    if (result.value) {
      loading.value = true;
      // Admin-API'den abonelik iptal
      await adminApi.post(`/subscriptions/${currentSubscription.value.id}/cancel`, {
        reason: result.value.trim()
      });
      ElNotification.success(t('subscription.messages.cancelSuccess'));
      await fetchSubscription();
    }
  } catch (err) {
    if (err !== 'cancel') {
      ElNotification.error(t('subscription.messages.cancelError'));
    }
  } finally {
    loading.value = false;
  }
};

const handleActivateSubscription = async () => {
  ElMessageBox.confirm(t('subscription.messages.activateConfirm'), t('subscription.messages.activateTitle'), {
    confirmButtonText: t('common.yes'),
    cancelButtonText: t('common.cancel'),
    type: 'warning',
    customClass: 'high-z-dialog',
    appendTo: 'body'
  }).then(async () => {
    try {
      // Admin-API'den abonelik aktifleştirme
      await adminApi.post(`/subscriptions/${currentSubscription.value?.id}/activate`);
      ElNotification.success(t('subscription.messages.activateSuccess'));
      
      // F5 gibi sayfayı yenile
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      ElNotification.error(t('subscription.messages.activateError'));
    }
  });
};


const handlePriceChange = () => {
  if (selectedPrice.value) {
    editForm.value.newUserCount = selectedPrice.value.min_user_count;
  }
};

const priceDifference = ref<number>(0);
const userDiff = ref<number>(0);
const daysDiff = ref<number>(0);

const filteredPriceList = computed(() => {
  if (!currentSubscription.value) return [];
  return priceList.value.filter(price =>
    price.id >= currentSubscription.value.price.id
  );
});

const calculatePriceDifference = () => {
  if (currentSubscription.value?.user_count == editForm.value.newUserCount) {
    priceDifference.value = 0;
    return;
  }
  
  userDiff.value = editForm.value.newUserCount - currentSubscription.value.user_count;

  // Calculate days difference properly
  const endDate = new Date(currentSubscription.value.end_date);
  const currentDate = new Date();
  daysDiff.value = Math.ceil((endDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));

  let userPricePerDay = (selectedPrice.value?.price || 0) * 12 / 365;
  priceDifference.value = userDiff.value * userPricePerDay * daysDiff.value;
};

const handleConfirmEdit = async () => {
  if (!currentSubscription.value || !editForm.value.selectedPriceId) return;

  try {
    loading.value = true;
    const requestData = {
      company_id: currentSubscription.value.company_id,
      subscription_id: currentSubscription.value.id,
      new_price_id: editForm.value.selectedPriceId,
      new_user_count: editForm.value.newUserCount,
      user_diff: userDiff.value,
      price_diff: priceDifference.value,
      days_diff: daysDiff.value,
    };
    
    // Admin-API'den kullanıcı sayısı güncelleme
    const response = await adminApi.post(`/subscriptions/${currentSubscription.value.id}/upgrade-user-count`, requestData);

    if (response.data.success) {
      ElNotification.success({
        title: t('common.success'),
        message: t('subscription.messages.updateSuccess')
      });
      editDialogVisible.value = false;
      await fetchSubscription();
      await fetchOrders();
    }
  } catch (error) {
    console.error('Error in handleConfirmEdit:', error);
    ElNotification.error({
      title: t('common.error'),
      message: t('subscription.messages.updateError')
    });
  } finally {
    loading.value = false;
  }
};

const renewalDurationDialogVisible = ref(false);
const renewalDurationForm = ref({
  newDurationUnit: null as PackageDurationType | null
});

// Print dialog variables
const printDialogVisible = ref(false);
const selectedOrderForPrint = ref<Order | null>(null);
const selectedOrderPaymentDetails = ref<any>(null);
const printing = ref(false);

// Payment Method Selection Dialog
const paymentMethodDialogVisible = ref(false);
const selectedPaymentMethod = ref<'vpos' | 'iban' | null>(null);
const posList = ref<any[]>([]);
const currentExchangeRate = ref<number | null>(null);

// TRY karşılığı (sipariş dövizi TRY değilse)
const selectedOrdersTotalTry = computed(() => {
  if (orderCurrencyCode.value === 'TRY' || !currentExchangeRate.value) {
    return null;
  }
  return selectedOrdersTotal.value * currentExchangeRate.value;
});
const hasPosSettings = computed(() => {
  if (!posList.value || posList.value.length === 0) {
    return false;
  }
  // Varsayılan (is_default: true) olan bir POS olup olmadığını kontrol et
  return posList.value.some((pos: any) => pos.is_default === true);
});

// IBAN Payment Dialog
const ibanPaymentDialogVisible = ref(false);
const ibanPaymentData = ref<any>(null);
const sendingNotification = ref(false);
const ibanPaymentForm = ref({
  paymentDate: new Date().toISOString().split('T')[0],
  receiptNumber: '',
  selectedAccount: 'TRY' as 'TRY' | 'USD'
});

const compactView = ref(false);

const openEditDialog = async () => {
  editForm.value.newUserCount = currentSubscription.value?.user_count || 0;
  editForm.value.selectedPriceId = currentSubscription.value?.price?.id || null;
  editDialogVisible.value = true;
  
  // Ensure the selected price is properly set after dialog opens
  await nextTick();
};

const openRenewalDialog = () => {
  renewalDurationForm.value.newDurationUnit = currentSubscription.value?.duration_unit || PackageDurationType.MONTH;
  renewalDurationDialogVisible.value = true;
};

const handleConfirmRenewalDuration = async () => {
  if (!currentSubscription.value || !renewalDurationForm.value.newDurationUnit) return;

  ElMessageBox.confirm(t('subscription.messages.renewalConfirm'), t('subscription.messages.renewalTitle'), {
    confirmButtonText: t('common.yes'),
    cancelButtonText: t('common.cancel'),
    type: 'warning',
    customClass: 'high-z-dialog'
  }).then(async () => {
    try {
      loading.value = true;
      const requestData = {
        company_id: currentSubscription.value.company_id,
        subscription_id: currentSubscription.value.id,
        new_duration_unit: renewalDurationForm.value.newDurationUnit
      };
      
      // Admin-API'den yenileme periyodu güncelleme
      const response = await adminApi.post(`/subscriptions/${currentSubscription.value.id}/update-duration-unit`, requestData);

      if (response.data.success) {
        ElNotification.success(t('subscription.messages.renewalSuccess'));
        await fetchSubscription();
        await fetchOrders();
      } else {
        ElNotification.error(t('subscription.messages.renewalError'));
      }
    } catch (error) {
      console.error('Error in handleConfirmRenewalDuration:', error);
      ElNotification.error({
        title: t('common.error'),
        message: t('subscription.messages.renewalError')
      });
    } finally {
      loading.value = false;
      renewalDurationDialogVisible.value = false;
    }
  });
};

const handleSizeChange = (newSize: number) => {
  pageSize.value = newSize;
  currentPage.value = 1; // Sayfa boyutu değiştiğinde ilk sayfaya dön
};

const handleCurrentChange = (newPage: number) => {
  currentPage.value = newPage;
};

const selectedPrice = computed(() => {
  if (!priceList.value || !editForm.value.selectedPriceId) return null;
  return priceList.value.find(price => price.id === editForm.value.selectedPriceId);
});

const getUserCountRange = () => {
  if (!selectedPrice.value) return [];

  // Aktif çalışan sayısından başla (en az 1)
  // min_user_count fiyat kademesi için, ama kullanıcı aktif çalışan sayısına kadar azaltabilir
  let start_user_count = Math.max(1, employeeCount.value || 1);

  // Step değeri null ise varsayılan olarak 1 kullan
  const step = selectedPrice.value.step || 1;
  
  const range = [];
  for (let i = start_user_count; i <= selectedPrice.value.max_user_count; i += step) {
    range.push(i);
  }
  
  return range;
};

// Payment durumunu çeviren fonksiyon
const getPaymentStatusText = (status: string) => {
  const s = status?.toUpperCase?.() || '';
  switch (s) {
    case 'COMPLETED':
      return t('subscription.status.completed');
    case 'PENDING':
      return t('subscription.status.pending');
    case 'FAILED':
      return t('subscription.status.failed');
    default:
      return t('subscription.status.unknown');
  }
};

// Print dialog'u göster
const showPrintDialog = async (order: Order) => {
  selectedOrderForPrint.value = order;
  printDialogVisible.value = true;
  
  // Ödeme detaylarını getir (Admin-API)
  try {
    const response = await adminApi.get(`/orders/${order.id}/payment-details`);
    selectedOrderPaymentDetails.value = response.data;
  } catch (error) {
    console.error('Error fetching payment details:', error);
    selectedOrderPaymentDetails.value = null;
  }
};

// IBAN ödeme dialog'unu göster
const showIbanPaymentDialog = (ibanData: any) => {
  ibanPaymentData.value = ibanData;
  // Döviz koduna göre varsayılan hesabı seç
  const defaultAccount = ibanData.currency === 'TRY' ? 'TRY' : 'USD';
  ibanPaymentForm.value = {
    paymentDate: new Date().toISOString().split('T')[0],
    receiptNumber: '',
    selectedAccount: defaultAccount
  };
  ibanPaymentDialogVisible.value = true;
  loading.value = false;
};

// IBAN kopyala
const copyIban = async (iban: string) => {
  try {
    await navigator.clipboard.writeText(iban.replace(/\s/g, ''));
    ElNotification({
      title: t('subscription.ibanDialog.copied'),
      message: t('subscription.ibanDialog.ibanCopied'),
      type: 'success',
      duration: 2000
    });
  } catch (err) {
    console.error('IBAN kopyalama hatası:', err);
  }
};

// Döviz sembolü
const getCurrencySymbol = (code: string) => {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', TRY: '₺', RUB: '₽', CNY: '¥' };
  return symbols[code] || code;
};

// Ödeme bildirimi gönder
const sendPaymentNotification = async () => {
  try {
    if (!ibanPaymentForm.value.paymentDate) {
      ElNotification.warning(t('subscription.messages.selectPaymentDate'));
      return;
    }
    
    sendingNotification.value = true;
    
    const requestData = {
      companyId: authStore.user.id,
      orderIds: selectedOrders.value.map(order => order.id).join(','),
      amountDue: selectedOrders.value.reduce((sum, order) => sum + Number(order.order_amount), 0),
      paymentMethod: 'iban',
      selectedAccount: ibanPaymentForm.value.selectedAccount, // Kullanıcının seçtiği hesap
      paymentDate: ibanPaymentForm.value.paymentDate,
      receiptNumber: ibanPaymentForm.value.receiptNumber || null
    };
    
    // Admin-API'den ödeme bildirimi gönder
    const response = await adminApi.post('/subscriptions/payment-notification', requestData);
    
    if (response.data.success) {
      ElNotification({
        title: t('common.success'),
        message: t('subscription.messages.notificationSuccess'),
        type: 'success',
        duration: 5000,
        customClass: 'high-z-notification'
      });
      ibanPaymentDialogVisible.value = false;
      selectedOrders.value = [];
    } else {
      throw new Error(response.data.message || t('subscription.messages.notificationError'));
    }
  } catch (error: any) {
    console.error('Payment notification error:', error);
    
    let errorMessage = t('subscription.messages.notificationError');
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    ElNotification({
      title: 'Hata',
      message: errorMessage,
      type: 'error',
      duration: 5000,
      customClass: 'high-z-notification'
    });
  } finally {
    sendingNotification.value = false;
  }
};

// Yazdırma fonksiyonu
const printReceipt = () => {
  if (!selectedOrderForPrint.value) return;
  
  printing.value = true;
  
  let printContent = '';
  printContent += `
    <div class="print-header">
      <img src='https://app.passqr.com.tr/logo.png' alt='PassQR Logo' class='logo' />
      <h1>${t('subscription.receipt.title')}</h1>
    </div>
  `;
  
  if (selectedOrderForPrint.value) {
    printContent += `<div class="section"><h2>${t('subscription.receipt.orderInfo')}</h2><ul>`;
    printContent += `<li><strong>${t('subscription.receipt.orderNo')}:</strong> ${selectedOrderForPrint.value.id}</li>`;
    printContent += `<li><strong>${t('subscription.receipt.amount')}:</strong> ${formatCurrency(selectedOrderForPrint.value.order_amount)}</li>`;
    printContent += `<li><strong>${t('subscription.receipt.currency')}:</strong> ${selectedOrderForPrint.value.order_currency}</li>`;
    printContent += `<li><strong>${t('subscription.receipt.orderDate')}:</strong> ${formatDate(new Date(selectedOrderForPrint.value.order_date))}</li>`;
    if (selectedOrderForPrint.value.order_details?.description) printContent += `<li><strong>${t('subscription.receipt.description')}:</strong> ${selectedOrderForPrint.value.order_details.description}</li>`;
    if (selectedOrderForPrint.value.order_details?.amount) printContent += `<li><strong>${t('subscription.receipt.userCount')}:</strong> ${selectedOrderForPrint.value.order_details.amount}</li>`;
    if (selectedOrderForPrint.value.order_details?.price) printContent += `<li><strong>${t('subscription.receipt.unitPrice')}:</strong> ${formatCurrency(selectedOrderForPrint.value.order_details.price)}</li>`;
    printContent += `</ul></div>`;
  }
  
  if (selectedOrderPaymentDetails.value?.payments?.length > 0) {
    printContent += `<div class="section"><h2>${t('subscription.receipt.paymentDetails')}</h2>`;
    selectedOrderPaymentDetails.value.payments.forEach((payment: any) => {
      printContent += `<ul>`;
      if (payment.transaction_id) printContent += `<li><strong>${t('subscription.receipt.transactionId')}:</strong> ${payment.transaction_id}</li>`;
      if (payment.payment_date) printContent += `<li><strong>${t('subscription.receipt.paymentDate')}:</strong> ${formatDate(new Date(payment.payment_date))}</li>`;
      if (payment.maskedCreditCard) printContent += `<li><strong>${t('subscription.receipt.card')}:</strong> ${payment.maskedCreditCard}</li>`;
      if (payment.status) printContent += `<li><strong>${t('subscription.receipt.status')}:</strong> ${getPaymentStatusText(payment.status)}</li>`;
      if (payment.amount) printContent += `<li><strong>${t('subscription.receipt.amount')}:</strong> ${formatCurrency(payment.amount)}</li>`;
      printContent += `</ul>`;
    });
    printContent += `</div>`;
  }
  
  printContent += `<div class='footer-note'>${t('subscription.receipt.footer')}</div>`;
  
  const printWindow = window.open('', '', 'width=600,height=800');
  if (!printWindow) {
    printing.value = false;
    return;
  }
  
  printWindow.document.write(`
    <html>
      <head>
        <title>Ödeme Makbuzu - PassQR</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 2rem; background: #f8f9fa; }
          .print-header { text-align: center; margin-bottom: 2rem; }
          .logo { height: 60px; margin-bottom: 1rem; }
          h1 { color: #409eff; font-size: 2rem; margin: 0; }
          h2 { color: #303133; font-size: 1.2rem; margin-top: 2rem; margin-bottom: 0.5rem; }
          ul { list-style: none; padding: 0; margin: 0 0 1rem 0; }
          li { margin-bottom: 0.5rem; font-size: 1rem; }
          .section { background: #fff; border-radius: 8px; box-shadow: 0 2px 8px #0001; padding: 1.5rem; margin-bottom: 1.5rem; }
          .footer-note { color: #888; text-align: center; margin-top: 2rem; font-size: 0.95rem; border-top: 1px solid #eee; padding-top: 1rem; }
        </style>
      </head>
      <body>${printContent}</body>
    </html>
  `);
  
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
  
  printing.value = false;
};

const loadingSvg = `
  <path class="path" d="
    M 30 15
    L 28 17
    M 25.61 25.61
    A 15 15, 0, 0, 1, 15 30
    A 15 15, 0, 1, 1, 27.99 7.5
    L 15 15
  " style="stroke-width: 4px; fill: none"/>
`;

onMounted(async () => {
  try {
    loading.value = true;
    
    await Promise.all([
      fetchSubscription(),
      fetchOrders(),
      fetchEmployeeCount(),
      fetchPosList(),
      fetchPendingUpgrades(),
    ]);
  } catch (err) {
    console.error('Error loading data:', err);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.subscription-view {
  padding: 10px;
  min-height: calc(100vh - 100px);
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
}

.loading-container {
  min-height: 400px;
}

/* Dialog z-index fix for modal */
:deep(.el-dialog) {
  z-index: 2001 !important;
}

/* Additional fixes for dialogs inside modal */
:deep(.el-dialog__wrapper) {
  z-index: 2001 !important;
}

:deep(.el-message-box) {
  z-index: 2002 !important;
}

:deep(.el-overlay) {
  z-index: 2000 !important;
}

:deep(.el-message-box__wrapper) {
  z-index: 2002 !important;
}

:deep(.el-notification) {
  z-index: 2003 !important;
}

:deep(.el-form-item) {
  overflow: visible !important;
}

/* Radio button styles */
.user-count-options,
.duration-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background-color: var(--el-fill-color-blank);
}

/* Dialog body overflow fix */
:deep(.el-dialog__body) {
  max-height: 60vh;
  overflow-y: auto;
}

:deep(.user-count-radio),
:deep(.duration-radio) {
  margin-right: 0 !important;
  margin-bottom: 4px !important;
  padding: 8px 12px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background-color: var(--el-fill-color-light);
  transition: all 0.2s ease;
}

:deep(.user-count-radio:hover),
:deep(.duration-radio:hover) {
  background-color: var(--el-fill-color);
  border-color: var(--el-color-primary);
}

:deep(.user-count-radio.is-checked),
:deep(.duration-radio.is-checked) {
  background-color: var(--el-color-primary-light-9);
  border-color: var(--el-color-primary);
  color: var(--el-color-primary);
}

:deep(.el-radio__label) {
  font-weight: 500;
  color: var(--el-text-color-primary);
}

:deep(.user-count-radio .el-radio__label),
:deep(.duration-radio .el-radio__label) {
  font-size: 14px;
  line-height: 1.4;
}

/* Subscription Layout */
.subscription-content {
  padding: 0;
}

.subscription-summary {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}

/* Status Header */
.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-bottom: 1px solid #a7f3d0;
}

.status-header.cancelled,
.status-header.expired {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-bottom-color: #fecaca;
}

.status-header.inactive {
  background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%);
  border-bottom-color: #fde68a;
}

.status-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: #10b981;
  color: #fff;
}

.status-badge.cancelled,
.status-badge.expired {
  background: #ef4444;
}

.status-badge.inactive {
  background: #f59e0b;
}

.status-badge .status-icon {
  font-size: 14px;
}

.renewal-info {
  font-size: 13px;
  color: #475569;
}

.renewal-info strong {
  color: #1e293b;
}

.status-actions {
  display: flex;
  gap: 8px;
}

/* Info Cards Grid */
.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1px;
  background: #e8eaed;
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: #fff;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.card-icon.user-icon {
  background: #eff6ff;
  color: #3b82f6;
}

.card-icon.period-icon {
  background: #f0fdf4;
  color: #22c55e;
}

.card-icon.fee-icon {
  background: #fef3c7;
  color: #f59e0b;
}

.card-icon.dealer-icon {
  background: #f3e8ff;
  color: #a855f7;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.card-label {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.card-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.card-value.highlight {
  color: #059669;
  font-size: 18px;
}

.card-value-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-sub {
  font-size: 11px;
  color: #64748b;
}

.edit-btn {
  padding: 4px !important;
  font-size: 14px;
}

.dealer-contacts {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.contact-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  transition: all 0.2s;
}

.contact-link:hover {
  background: #3b82f6;
  color: #fff;
}

/* Pending Changes Section */
.pending-changes-section {
  padding: 0 20px 16px;
}

.pending-alert {
  margin-top: 0;
}

.pending-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pending-title {
  font-weight: 600;
  font-size: 13px;
}

.pending-list {
  margin-top: 8px;
}

.pending-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  margin-top: 8px;
}

.pending-item:first-child {
  margin-top: 0;
}

.pending-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pending-change {
  font-size: 13px;
  color: #1e293b;
}

.pending-amount {
  font-size: 11px;
  color: #92400e;
}

/* Orders Section */
.orders-section {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  overflow: hidden;
}

.orders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e8eaed;
}

.orders-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.orders-content {
  padding: 12px;
}

.orders-table-wrapper {
  overflow-x: auto;
}

.payment-summary-inline {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8eaed;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.summary-count {
  font-size: 12px;
  color: #64748b;
}

.summary-total {
  font-size: 14px;
  font-weight: 600;
  color: #059669;
}

.order-description {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.description-main {
  font-size: 0.9rem;
  line-height: 1.3;
  color: var(--el-text-color-primary);
}

.order-details-info {
  margin-top: 2px;
}

/* Compact table styles */
:deep(.el-table--small) {
  font-size: 0.85rem;
}

:deep(.el-table--small .el-table__header) {
  font-size: 0.8rem;
  font-weight: 600;
}

:deep(.el-table--small .el-table__row) {
  height: 40px;
}

:deep(.el-table--small .el-table__cell) {
  padding: 6px 8px;
}

:deep(.el-table--small .el-button) {
  padding: 4px;
  font-size: 0.8rem;
}

:deep(.el-table--small .el-tag) {
  font-size: 0.75rem;
  padding: 2px 6px;
}

/* Compact view specific styles */
.compact-view .order-description .order-details-info {
  display: none;
}

.compact-view :deep(.el-table__row) {
  height: 35px;
}

.compact-view :deep(.el-table__cell) {
  padding: 4px 6px;
}

/* Dialog styles */
.info-row {
  display: flex;
  flex-direction: column;
  gap: 5px;
  border: 1px solid var(--el-border-color-lighter);
  padding: 10px;
  border-radius: 5px;
  background-color: var(--el-fill-color-light) !important;
}

.label {
  color: #606266;
  font-size: 14px;
}

.value {
  font-weight: 500;
  color: #303133;
}

.payment-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.me-2 {
  margin-right: 8px;
}

.pagination-container {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.order-details-info {
  margin-top: 4px;
  font-size: 12px;
}

:deep(.paid-row) {
  background-color: var(--el-fill-color-light) !important;
  opacity: 0.7;
}

:deep(.paid-row .order-column) {
  color: var(--el-text-color-secondary) !important;
}

:deep(.unpaid-row) {
  background-color: #fef0f0 !important;
}

:deep(.unpaid-row .order-column) {
  color: #f56c6c !important;
}

:deep(.paid-row:hover > td) {
  background-color: var(--el-fill-color) !important;
  opacity: 0.8;
}

:deep(.unpaid-row:hover > td) {
  background-color: #fde2e2 !important;
}

/* Responsive styles */
@media (max-width: 768px) {
  .status-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .status-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .info-cards-grid {
    grid-template-columns: 1fr;
  }

  .payment-summary-inline {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .summary-info {
    justify-content: space-between;
  }
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.w-full {
  width: 100%;
}

.text-muted {
  color: var(--el-text-color-secondary);
  font-size: 0.875rem;
}

:deep(.el-input-number) {
  width: 100%;
}

:deep(.el-loading-spinner) {
  .path {
    stroke: var(--el-color-primary);
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
    stroke-width: 3px;
  }

  .el-loading-text {
    font-size: 18px;
    margin-top: 20px;
  }

  svg {
    width: 80px;
    height: 80px;
  }
}

:deep(.el-loading-mask) {
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 2100;
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

/* Print dialog styles */
.print-content {
  max-height: 60vh;
  overflow-y: auto;
}

.print-header {
  text-align: center;
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--el-color-success-light-9);
  border-radius: 8px;
  border-left: 4px solid var(--el-color-success);
}

.print-header h3 {
  margin: 0 0 5px 0;
  color: var(--el-color-success);
  font-size: 1.2rem;
}

.print-header p {
  margin: 0;
  color: var(--el-text-color-regular);
  font-size: 0.9rem;
}

.print-details {
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--el-fill-color-light);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
}

.print-details h4 {
  margin: 0 0 15px 0;
  color: var(--el-text-color-primary);
  font-size: 1rem;
  font-weight: 600;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 5px 0;
  border-bottom: 1px solid var(--el-border-color-extra-light);
}

.detail-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.detail-row .label {
  font-weight: 500;
  color: var(--el-text-color-regular);
  font-size: 0.9rem;
}

.detail-row .value {
  font-weight: 600;
  color: var(--el-text-color-primary);
  font-size: 0.9rem;
  text-align: right;
}

.payment-detail {
  margin-bottom: 15px;
  padding: 10px;
  background-color: var(--el-fill-color-blank);
  border-radius: 6px;
  border: 1px solid var(--el-border-color-light);
}

.payment-detail:last-child {
  margin-bottom: 0;
}

.me-2 {
  margin-right: 8px;
}

/* High z-index notification for dialogs */
:deep(.high-z-notification) {
  z-index: 10007 !important;
}

:deep(.el-notification) {
  z-index: 10007 !important;
}

:deep(.el-notification__group) {
  z-index: 10007 !important;
}

/* IBAN Payment Dialog Styles */
.iban-payment-content {
  max-height: 70vh;
  overflow-y: auto;
}

.payment-info h3 {
  margin: 0 0 20px 0;
  color: var(--el-color-primary);
  font-size: 1.3rem;
  font-weight: 600;
}

.info-section {
  margin-bottom: 15px;
  padding: 15px;
  background-color: var(--el-fill-color-light);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
}

.info-section h4 {
  margin: 0 0 15px 0;
  color: var(--el-text-color-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  border-bottom: 1px solid var(--el-border-color-light);
}

.info-item:last-child {
  border-bottom: none;
}

.info-item .label {
  font-weight: 500;
  color: var(--el-text-color-regular);
  font-size: 0.9rem;
}

.info-item .value {
  font-weight: 600;
  color: var(--el-text-color-primary);
  font-size: 0.9rem;
  text-align: right;
}

.iban-number {
  font-family: 'Courier New', monospace;
  font-size: 1rem !important;
  letter-spacing: 1px;
  color: var(--el-color-primary) !important;
}

/* IBAN Accounts Styles */
.iban-accounts {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Compact IBAN Payment Dialog */
.iban-payment-content.compact {
  padding: 0;
}

.iban-payment-content.compact .payment-info {
  padding: 0;
}

.order-summary-compact {
  background: var(--el-fill-color-light);
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.order-summary-compact .summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  padding: 3px 0;
}

.order-summary-compact .summary-row.total {
  font-weight: 600;
  font-size: 1rem;
  border-top: 1px solid var(--el-border-color);
  margin-top: 5px;
  padding-top: 8px;
}

.order-summary-compact .try-equivalent,
.selected-amount-info .try-equivalent {
  color: var(--el-text-color-secondary);
  font-weight: 400;
  font-size: 0.85rem;
  margin-left: 5px;
}

.iban-section {
  margin-bottom: 15px;
}

.iban-card {
  background: white;
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  padding: 12px 15px;
  margin-bottom: 10px;
}

.iban-card.selectable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.iban-card.selectable:hover {
  border-color: var(--el-color-primary-light-5);
  background: var(--el-color-primary-light-9);
}

.iban-card.selectable.selected {
  border-color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  box-shadow: 0 0 0 2px var(--el-color-primary-light-7);
}

.iban-card:last-child {
  margin-bottom: 0;
}

.iban-select-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.iban-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.bank-info {
  font-size: 0.85rem;
  color: var(--el-text-color-secondary);
}

.iban-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.iban-text {
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  color: var(--el-color-primary);
  letter-spacing: 0.5px;
}

.iban-amount-display {
  margin-top: 10px;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--el-color-success);
  text-align: right;
}

.iban-amount-display .try-info {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--el-text-color-secondary);
}

.payment-form-compact {
  background: var(--el-fill-color-light);
  padding: 12px 15px;
  border-radius: 8px;
}

.payment-form-compact .el-form-item {
  margin-bottom: 0;
}

.iban-account-item {
  background: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  padding: 12px 15px;
}

.iban-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.currency-badge {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.currency-badge.try {
  background: #e8f5e9;
  color: #2e7d32;
}

.currency-badge.usd {
  background: #e3f2fd;
  color: #1565c0;
}

.iban-amount {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--el-color-success);
}

.iban-number-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.iban-label {
  font-size: 0.85rem;
  color: var(--el-text-color-secondary);
  font-weight: 500;
}

.iban-value {
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  letter-spacing: 1px;
  color: var(--el-color-primary);
  flex: 1;
}

.amount {
  color: var(--el-color-success) !important;
  font-size: 1.1rem !important;
}

.order-details p {
  margin: 8px 0;
  font-size: 0.9rem;
  line-height: 1.4;
  border-bottom: 1px solid var(--el-border-color-light);
}

.payment-instructions {
  background-color: var(--el-color-warning-light-9);
  border: 1px solid var(--el-color-warning-light-7);
  border-radius: 8px;
  padding: 15px;
}

.payment-instructions h4 {
  margin: 0 0 15px 0;
  color: var(--el-color-warning-dark-2);
  font-size: 1rem;
  font-weight: 600;
}

.payment-instructions ol {
  margin: 0;
  padding-left: 20px;
}

.payment-instructions li {
  margin: 8px 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--el-text-color-regular);
}

.alert-content p {
  margin: 0;
  font-size: 0.9rem;
}

/* Payment Method Selection Dialog Styles */
.payment-method-selection {
  padding: 10px 0;
}

.payment-method-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

.payment-method-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border: 2px solid var(--el-border-color-lighter);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--el-fill-color-blank);
  position: relative;
}

.payment-method-card:hover {
  border-color: var(--el-color-primary-light-7);
  background-color: var(--el-color-primary-light-9);
}

.payment-method-card.selected {
  border-color: var(--el-color-primary);
  background-color: var(--el-color-primary-light-9);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.payment-method-icon {
  margin-right: 15px;
  color: var(--el-color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background-color: var(--el-color-primary-light-9);
  border-radius: 50%;
}

.payment-method-card.selected .payment-method-icon {
  background-color: var(--el-color-primary);
  color: white;
}

.payment-method-info {
  flex: 1;
}

.payment-method-info h3 {
  margin: 0 0 5px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.payment-method-info p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--el-text-color-secondary);
}

.payment-method-radio {
  margin-left: 15px;
}

.selected-amount-info {
  padding: 15px;
  background-color: var(--el-fill-color-light);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.amount-row .label {
  font-weight: 500;
  color: var(--el-text-color-regular);
  font-size: 0.95rem;
}

.amount-row .value {
  font-weight: 600;
  color: var(--el-color-primary);
  font-size: 1.2rem;
}

.amount-row.try-row {
  margin-top: 4px;
}

.amount-row .try-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--el-text-color-secondary);
}

</style>